import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { PATHS } from 'src/common/constants';
import { selectAuthenticatedUser } from 'src/store/selectors/auth';
import {
  useNotesAPI,
  UseNotesAPIReturn,
} from 'src/SharedCustomHooks/UseNotesAPI/useNotesAPI';
import Breadcrumb from '../../components/Breadcrumb';
import Button from '../../components/Button';
import Card from '../../components/Card';
import HeadingWithDetails from '../../components/HeadingWithDetails';
import FontAwesomeIcon from '../../components/Icon';
import ItemDocuments from './ItemDocuments';
import ItemMaintenanceGuide from './ItemMaintenanceGuide';
import ItemFaqs from './ItemFaqs';
import ItemForm from '../../components/ItemForm';
import ItemNotes from './ItemNotes';
import ItemCommonIssues from './ItemCommonIssues';
import Skeleton from 'src/components/Skeleton/Skeleton';
import { getItemTabSkeleton } from 'src/pages/Item/skeletons/ItemTabSkeleton';
import Metrics from '../../components/Metrics';
import Tabs from '../../components/Tabs';
import Warranty from '../../components/Warranty';
import ItemServiceRequest from './ItemServiceRequest';
import ItemSpecifications from './ItemSpecifications';
import useItemOrchestrator, {
  UseItemOrchestratorReturn,
} from './Hooks/useItemOrchestrator';
import WithNavigation from 'src/layouts/WithNavigation/WithNavigation';

const HIDDEN_NOTES_ROLES = new Set<string>([
  'Occupant',
  'Realtor',
  'Home Owner',
]);

const ItemInner = (props: any) => {
  const user: any = useSelector(selectAuthenticatedUser);
  const itemId: string = props.match.params.id;

  const notesAPI: UseNotesAPIReturn = useNotesAPI(user);

  useEffect(() => {
    if (itemId) {
      void notesAPI.fetchNotes(itemId);
    }
  }, [itemId]);

  const { notes } = notesAPI;

  const {
    item,
    refetchItem,
    commonIssues,
    branding,
    roleName,

    showOverlay,
    onOpenEditForm,
    onCloseEditForm,
    onSaveItem,
    onDeleteItem,

    faqOperations,

    hasHomeOwnerVisibleNote,

    documents,
    loadingDocs,
    isSavingDoc,
    saveDocument,
    deleteDocument,

    maintenanceGuides,
    maintenanceGuidesLoading,
    saveGuide,
    updateGuide,
    deleteGuide,
    maintenanceGuidesError,
    searchTerm,
    setSearchTerm,

    specificationsAPI,
  }: UseItemOrchestratorReturn = useItemOrchestrator({
    itemId,
    history: props.history,
    notes,
  });

  const loadingItem: boolean = !item;
  const { faqs } = faqOperations;

  const shouldHideNotesTab: boolean =
    HIDDEN_NOTES_ROLES.has(roleName) && hasHomeOwnerVisibleNote === false;

  const canEditOrDelete: boolean = roleName !== 'Strata Manager';

  const skeletonBadge = <Skeleton variant="text" width="1.4rem" />;

  const headlineNode: React.ReactNode = loadingItem ? (
    <Skeleton variant="text" width="14rem" />
  ) : (
    item.name
  );

  const detailItems: React.ReactNode[] = loadingItem
    ? [
        <Card
          body={
            <>
              MAKE: <Skeleton variant="text" width="6rem" /> <br />
              MODEL: <Skeleton variant="text" width="6rem" /> <br />
              TYPE: <Skeleton variant="text" width="6rem" />
            </>
          }
        />,
        <Metrics
          items={[{ value: <Skeleton variant="text" width="2rem" /> }]}
          title={null}
        />,
        <Card
          title="Warranty Plan"
          body={<Skeleton variant="text" width="6rem" />}
        />,
      ]
    : [
        <Card
          body={
            <>
              MAKE: {item.makeName ? item.makeName : '—'} <br />
              MODEL: {item.model ? item.model : '—'} <br />
              TYPE: {item.typeName}
            </>
          }
        />,
        <Metrics
          items={[{ value: item.serviceRequestsCount }]}
          title={null}
        />,
        <Card
          title="Warranty Plan"
          body={<Warranty duration={item.warrantyYears} />}
        />,
      ];

  const tabItems: any[] = loadingItem
    ? [
        { label: 'Specifications', count: skeletonBadge, content: getItemTabSkeleton('Specifications') },
        { label: 'FAQs', count: skeletonBadge, content: getItemTabSkeleton('FAQs') },
        { label: 'Manuals & Documents', count: skeletonBadge, content: getItemTabSkeleton('Manuals & Documents') },
        { label: branding.text.serviceRequest + 's', count: skeletonBadge, content: getItemTabSkeleton(branding.text.serviceRequest + 's') },
      ]
    : [
        {
          label: 'Specifications',
          count: item.faq_count,
          content: (
            <ItemSpecifications
              item={item}
              specificationsAPI={specificationsAPI}
            />
          ),
        },
        {
          label: 'FAQs',
          count: faqs ? faqs.length.toString() : '0',
          content: <ItemFaqs item={item} faqOperations={faqOperations} />,
        },
        shouldHideNotesTab
          ? {}
          : {
              label: 'Notes',
              count: notes?.length > 0 ? notes.length : '',
              content: (
                <ItemNotes
                  notesAPI={notesAPI}
                  entityId={itemId}
                  afterMutate={refetchItem}
                />
              ),
            },
        {
          label: 'Manuals & Documents',
          count: documents.length.toString(),
          content: (
            <ItemDocuments
              item={item}
              documents={documents}
              loadingDocs={loadingDocs}
              isSaving={isSavingDoc}
              saveDocument={saveDocument}
              deleteDocument={deleteDocument}
            />
          ),
        },
        {
          label: branding.text.serviceRequest + 's',
          count: item.document_count,
          content: <ItemServiceRequest item={item} />,
        },
        {
          label: 'Maintenance Reminders',
          count: maintenanceGuides.length.toString(),
          content: (
            <ItemMaintenanceGuide
              item={item}
              maintenanceGuides={maintenanceGuides}
              loading={maintenanceGuidesLoading}
              saveGuide={saveGuide}
              updateGuide={updateGuide}
              deleteGuide={deleteGuide}
              error={maintenanceGuidesError}
              setSearchTerm={setSearchTerm}
              searchTerm={searchTerm}
            />
          ),
        },
        {
          label: 'Common Issues',
          count: commonIssues.length.toString(),
          content: (
            <ItemCommonIssues item={item} commonIssues={commonIssues} />
          ),
        },
      ];

  return (
    <WithNavigation
      title={loadingItem ? 'Item' : item.name}
      top={
        <HeadingWithDetails
          breadcrumb={<Breadcrumb to={PATHS.ITEMS_LIST} label="Catalog" />}
          headline={headlineNode}
          items={detailItems}
          actions={
            !loadingItem && canEditOrDelete ? (
              <>
                <Button
                  color="semantic"
                  onClick={onOpenEditForm}
                  label={<FontAwesomeIcon glyph="pencil" weight="light" />}
                />
                <Button
                  color="semantic"
                  onClick={onDeleteItem}
                  label={<FontAwesomeIcon glyph="trash" weight="light" />}
                />
                {showOverlay && (
                  <ItemForm
                    onClose={onCloseEditForm}
                    onSaveItem={onSaveItem}
                  />
                )}
              </>
            ) : null
          }
        />
      }
      bottom={<Tabs from={'Catalog'} items={tabItems} />}
    />
  );
};

const Item = (props: any) => {
  return <ItemInner {...props} />;
};

export default Item;
